import React from 'react';

import {
  Alert,
  AlertIcon,
  Badge,
  Button,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react';
import { customTheme } from 'theme';

import { Policy } from 'api/shared/mfa/v1/api.pb';
import { SecurityIcon } from 'components/Icons';
import { OrgConfigBox } from 'containers/Settings/Components/OrganizationConfig/OrgConfigBox';
import { renderOrgSettingHeader } from 'containers/Settings/Components/OrganizationConfig/utils';

// Consume the client generated by the shared APIs library.

import { getMFAError, needsMFAReauthentication } from '../mfaUtils';
import { useMFAEnrollment } from '../useMFAEnrollment';

// Keycloak performs QR setup and verification; this screen only shows confirmed credential status.
export const Security = () => {
  const {
    data,
    error,
    isPending,
    refetch,
    isStarting,
    actionError,
    enable,
    reauthenticate,
  } = useMFAEnrollment();

  return (
    <OrgConfigBox maxW="700px">
      <Stack spacing={4}>
        {renderOrgSettingHeader({
          icon: <SecurityIcon />,
          header: 'Two-factor authentication',
          subHeader:
            'Add a verification code from your authenticator app when signing in.',
        })}
        {(error || actionError) && (
          <Alert status="error">
            <AlertIcon />
            {actionError || getMFAError(error)}
          </Alert>
        )}
        {isPending && <Text role="status">Loading security settings…</Text>}
        {data && (
          <>
            <HStack>
              <Text color={customTheme.colors.gray[300]}>Status</Text>
              <Badge
                color={
                  data.enabled
                    ? customTheme.colors.green[300]
                    : customTheme.colors.gray[300]
                }
                bg={
                  data.enabled
                    ? customTheme.colors.green[50]
                    : customTheme.colors.gray[50]
                }
                borderRadius={4}
                px={2}
                py={1}
              >
                {data.enabled ? 'Enabled' : 'Not enabled'}
              </Badge>
            </HStack>
            {data.policy === Policy.REQUIRED && (
              <Alert status={data.enforcement_active ? 'info' : 'warning'}>
                <AlertIcon />
                {data.enforcement_active
                  ? 'Required by your organization. You must keep an authenticator configured.'
                  : 'Your organization requires two-factor authentication, but policy setup needs administrator attention.'}
              </Alert>
            )}
            <Text>
              {data.enabled
                ? 'Your authenticator app is configured. If you lose access to your device, ask an organization administrator to reset two-factor authentication for your account.'
                : 'You will scan a QR code and enter a verification code to finish setup.'}
            </Text>
            <HStack>
              {!data.enabled && (
                <Button
                  bg={customTheme.colors.primary}
                  color="white"
                  _hover={{ opacity: 0.85 }}
                  onClick={enable}
                  isLoading={isStarting}
                >
                  Enable two-factor authentication
                </Button>
              )}
              <Button variant="ghost" onClick={() => refetch()}>
                Refresh status
              </Button>
            </HStack>
          </>
        )}
        {error && (
          <Button
            onClick={
              needsMFAReauthentication(error) ? reauthenticate : () => refetch()
            }
          >
            {needsMFAReauthentication(error) ? 'Sign in again' : 'Retry'}
          </Button>
        )}
      </Stack>
    </OrgConfigBox>
  );
};
